// frontend/src/components/common/RichTextDisplay.jsx
// Component to safely display rich text HTML content
// No external dependencies - uses built-in browser sanitization

import React, { useMemo } from "react";

const RichTextDisplay = ({ content, className = "" }) => {
  // Simple HTML sanitizer using browser's built-in DOMParser
  const sanitizedContent = useMemo(() => {
    if (!content) return "";

    // Create a temporary DOM to parse and sanitize
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");

    // Remove potentially dangerous elements
    const dangerousTags = ["script", "iframe", "object", "embed", "form", "input", "button"];
    dangerousTags.forEach((tag) => {
      const elements = doc.querySelectorAll(tag);
      elements.forEach((el) => el.remove());
    });

    // Remove dangerous attributes from all elements
    const allElements = doc.body.querySelectorAll("*");
    allElements.forEach((el) => {
      // Remove event handlers (onclick, onerror, etc.)
      const attrs = Array.from(el.attributes);
      attrs.forEach((attr) => {
        if (attr.name.startsWith("on") || attr.name === "srcdoc") {
          el.removeAttribute(attr.name);
        }
        // Clean javascript: URLs
        if (attr.value && attr.value.toLowerCase().includes("javascript:")) {
          el.removeAttribute(attr.name);
        }
      });

      // Make links open in new tab safely
      if (el.tagName === "A") {
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer");
      }
    });

    return doc.body.innerHTML;
  }, [content]);

  // If no content, show placeholder
  if (!content || content.trim() === "" || content === "<p><br></p>" || content === "<br>") {
    return (
      <div className={`rich-text-content text-muted ${className}`}>
        <em>No content</em>
      </div>
    );
  }

  return (
    <div
      className={`rich-text-content ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

export default RichTextDisplay;